import { decrypt } from "../../../../utils/other";
import { NATIVE_MESSAGE_TYPE } from "./constants";

/**
 * The one place that knows how StudentPortal detects its React Native WebView host.
 * Sandbox has no `isApp` key, so migrating this feature changes only this file.
 */
export const hasNativeBridge = () =>
  typeof window !== "undefined" && Boolean(window.ReactNativeWebView);

export const isNativeApp = () =>
  hasNativeBridge() && localStorage.getItem("isApp") === "true";

// V2's isLoggedIn: inside the WebView with a stored session token.
export const hasNativeSession = () =>
  hasNativeBridge() && decrypt(localStorage.getItem("user"))?.ACCESS_TOKEN?.length > 0;

// The host can close its web view while the page still runs (iOS then has no
// `webkit.messageHandlers.ReactNativeWebView` and postMessage throws). Nobody
// is left to receive the message, so the failure is ignored.
export const postToNative = (message) => {
  if (!hasNativeBridge()) return;
  try {
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
  } catch (error) {
    // Host gone: nothing to tell.
  }
};

export const postRouteToNative = () => postToNative({ type: NATIVE_MESSAGE_TYPE.ROUTE });

export const postSlotSelectorState = (isVisible) =>
  postToNative({ type: NATIVE_MESSAGE_TYPE.SLOT_SELECTOR_STATE, isVisible });

/**
 * Listen for JSON messages from the native host. Non-JSON messages (e.g. analytics SDKs) are ignored.
 * @param {(message: Object) => void} onMessage
 * @returns {() => void} unsubscribe
 */
export const subscribeToNativeMessages = (onMessage) => {
  if (!hasNativeBridge()) return () => {};

  const handleMessage = (event) => {
    if (typeof event?.data !== "string" || !event.data.startsWith("{")) return;
    try {
      onMessage(JSON.parse(event.data));
    } catch (error) {
      // A message that only looks like JSON is not ours; V2 ignored these too.
    }
  };

  window.addEventListener("message", handleMessage);
  return () => window.removeEventListener("message", handleMessage);
};
